// Translucent virtual joystick overlay for mobile aiming. Shows an outer
// "power ring" at the initial touch point and a "knob" at the current
// touch point. Gives visual feedback for where you're pulling and how hard.
//
// Purely cosmetic — the real aim calculation lives in aim.ts. This module
// just listens to the same pointer events and renders a DOM overlay.

// Must match MAX_PIXELS_MOBILE/DESKTOP in aim.ts so the visual ring matches
// when the player hits full power.
const MAX_PIXELS_DESKTOP = 180;
const MAX_PIXELS_MOBILE = 130;
const SHOW_THRESHOLD = 8; // px — matches aim.ts MIN_PIXELS

export interface Joystick {
  dispose(): void;
}

export function installJoystick(canvas: HTMLElement): Joystick {
  const isCoarse = matchMedia("(pointer: coarse)").matches;
  const maxPx = isCoarse ? MAX_PIXELS_MOBILE : MAX_PIXELS_DESKTOP;

  const root = document.createElement("div");
  root.id = "joystick";
  const outer = document.createElement("div");
  outer.className = "joy-outer";
  const knob = document.createElement("div");
  knob.className = "joy-knob";
  const line = document.createElement("div");
  line.className = "joy-line";
  root.append(line, outer, knob);
  document.body.appendChild(root);

  let startX = 0;
  let startY = 0;
  let pointerId: number | null = null;
  let active = false;

  const hide = () => {
    if (!active) return;
    active = false;
    root.classList.remove("active");
  };

  const paint = (x: number, y: number) => {
    const dx = x - startX;
    const dy = y - startY;
    const d = Math.hypot(dx, dy);
    if (d < SHOW_THRESHOLD) {
      // Still below deadzone — render the outer ring at start so the user
      // sees feedback immediately, but no knob/line yet.
      outer.style.transform = `translate(${startX}px, ${startY}px) translate(-50%, -50%) scale(0.7)`;
      knob.style.opacity = "0";
      line.style.opacity = "0";
      if (!active) {
        active = true;
        root.classList.add("active");
      }
      return;
    }
    const t = Math.min(1, d / maxPx);
    // Clamp knob to ring edge when pulling past full power.
    const ux = dx / d;
    const uy = dy / d;
    const clampedD = Math.min(d, maxPx);
    const kx = startX + ux * clampedD;
    const ky = startY + uy * clampedD;

    outer.style.transform = `translate(${startX}px, ${startY}px) translate(-50%, -50%) scale(${0.85 + t * 0.15})`;
    outer.style.opacity = String(0.55 + t * 0.35);
    knob.style.transform = `translate(${kx}px, ${ky}px) translate(-50%, -50%)`;
    knob.style.opacity = "0.95";

    // Line connecting center to knob. Use a thin 4px-tall bar rotated to
    // direction, length = clampedD.
    const angle = Math.atan2(uy, ux);
    const midX = (startX + kx) / 2;
    const midY = (startY + ky) / 2;
    line.style.transform = `translate(${midX}px, ${midY}px) translate(-50%, -50%) rotate(${angle}rad) scaleX(${clampedD})`;
    line.style.opacity = String(0.35 + t * 0.35);

    if (!active) {
      active = true;
      root.classList.add("active");
    }
  };

  const onDown = (e: PointerEvent) => {
    if (pointerId !== null) return;
    pointerId = e.pointerId;
    startX = e.clientX;
    startY = e.clientY;
    paint(e.clientX, e.clientY);
  };

  const onMove = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return;
    paint(e.clientX, e.clientY);
  };

  const onEnd = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return;
    pointerId = null;
    hide();
  };

  canvas.addEventListener("pointerdown", onDown, { passive: true });
  canvas.addEventListener("pointermove", onMove, { passive: true });
  canvas.addEventListener("pointerup", onEnd, { passive: true });
  canvas.addEventListener("pointercancel", onEnd, { passive: true });

  return {
    dispose() {
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onEnd);
      canvas.removeEventListener("pointercancel", onEnd);
      root.remove();
    },
  };
}
